/**
 * Client-side CSV export.
 *
 * Shared across admin pages that offer "Export CSV" per the spec conventions
 * (§"Cross-cutting conventions" in docs/specs/README.md). For large datasets
 * (> 10k rows) real endpoint should stream from the Worker instead.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-19-v1
 */

const escape = (v: unknown): string => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
};

export function toCsv<T extends Record<string, unknown>>(rows: T[], headers?: (keyof T)[]): string {
    if (rows.length === 0) return '';
    const cols = headers ?? (Object.keys(rows[0]) as (keyof T)[]);
    const head = cols.map((c) => escape(String(c))).join(',');
    const body = rows.map((r) => cols.map((c) => escape(r[c])).join(',')).join('\n');
    return `${head}\n${body}`;
}

export function downloadCsv(filename: string, csv: string) {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export function exportCsv<T extends Record<string, unknown>>(
    filename: string,
    rows: T[],
    headers?: (keyof T)[]
) {
    downloadCsv(filename, toCsv(rows, headers));
}
